// Utility function to calculate user badges based on their played courses
export const calculateUserBadges = (playedCourses, allCourses) => {
  if (!playedCourses || !playedCourses.length || !allCourses || !allCourses.length) {
    return {
      nationalBadge: null,
      publicBadge: null,
      stateBadge: null
    };
  }

  // Get course info for played courses
  const courseInfo = playedCourses.map(id => allCourses.find(c => c.course_id === id)).filter(Boolean);
  
  // Calculate badge counts for each category
  const top1National = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 1).length;
  const top10National = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 10).length;
  const top25National = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 25).length;
  const top50National = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 50).length;
  const top100National = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 100).length;

  const top1Public = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 1).length;
  const top10Public = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 10).length;
  const top25Public = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 25).length;
  const top50Public = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 50).length;
  const top100Public = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 100).length;

  const top1State = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 1).length;
  const top10State = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 10).length;
  const top25State = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 25).length;
  const top50State = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 50).length;
  const top100State = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 100).length;

  // Determine best badge for each category (highest priority first)
  let nationalBadge = null;
  if (top1National > 0) nationalBadge = { type: 'top1all', count: top1National, label: 'America Top 1' };
  else if (top10National > 0) nationalBadge = { type: 'top10all', count: top10National, label: 'America Top 10' };
  else if (top25National > 0) nationalBadge = { type: 'top25all', count: top25National, label: 'America Top 25' };
  else if (top50National > 0) nationalBadge = { type: 'top50all', count: top50National, label: 'America Top 50' };
  else if (top100National > 0) nationalBadge = { type: 'top100all', count: top100National, label: 'America Top 100' };

  let publicBadge = null;
  if (top1Public > 0) publicBadge = { type: 'top1pub', count: top1Public, label: 'Public Top 1' };
  else if (top10Public > 0) publicBadge = { type: 'top10pub', count: top10Public, label: 'Public Top 10' };
  else if (top25Public > 0) publicBadge = { type: 'top25pub', count: top25Public, label: 'Public Top 25' };
  else if (top50Public > 0) publicBadge = { type: 'top50pub', count: top50Public, label: 'Public Top 50' };
  else if (top100Public > 0) publicBadge = { type: 'top100pub', count: top100Public, label: 'Public Top 100' };

  let stateBadge = null;
  if (top1State > 0) stateBadge = { type: 'top1state', count: top1State, label: 'State Top 1' };
  else if (top10State > 0) stateBadge = { type: 'top10state', count: top10State, label: 'State Top 10' };
  else if (top25State > 0) stateBadge = { type: 'top25state', count: top25State, label: 'State Top 25' };
  else if (top50State > 0) stateBadge = { type: 'top50state', count: top50State, label: 'State Top 50' };
  else if (top100State > 0) stateBadge = { type: 'top100state', count: top100State, label: 'State Top 100' };

  return {
    nationalBadge,
    publicBadge,
    stateBadge
  };
};

// Component to render user badges
export const UserBadges = ({ badges, size = 'small' }) => {
  if (!badges || (!badges.nationalBadge && !badges.publicBadge && !badges.stateBadge)) {
    return null;
  }

  const badgeSize = size === 'small' ? '20px' : '70px';
  const containerStyle = {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    marginLeft: '8px'
  };

  const badgeStyle = {
    width: badgeSize,
    height: badgeSize,
    objectFit: 'contain'
  };

  return (
    <div style={containerStyle}>
      {badges.nationalBadge && (
        <img
          src={`/images/${badges.nationalBadge.type}_badge.png`}
          alt={badges.nationalBadge.label}
          style={badgeStyle}
          title={badges.nationalBadge.label}
        />
      )}
      {badges.publicBadge && (
        <img
          src={`/images/${badges.publicBadge.type}_badge.png`}
          alt={badges.publicBadge.label}
          style={badgeStyle}
          title={badges.publicBadge.label}
        />
      )}
      {badges.stateBadge && (
        <img
          src={`/images/${badges.stateBadge.type}_badge.png`}
          alt={badges.stateBadge.label}
          style={badgeStyle}
          title={badges.stateBadge.label}
        />
      )}
    </div>
  );
};
